import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { selectCurrentUserProfilePicture } from '../../services/community/identity/profile/redux/selectors'
import { getBoundActions } from '../../actions/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { chatListStoreKey } from '../../services/live-access-events/reducers'
import { LIVE_ACCESS_SUBSCRIPTION_CODE } from '../../services/live-access-events/constants'
import { setLocalStorage, getLocalStorage } from '../../services/local-preferences/index'
import Chat from '../EventsPage/Chat/index'
import { isBrowser } from '../../config/environment'
import useCommunityEnabled from '../../services/community/shared/useCommunityEnabled'
import LiveExperienceVideo from './LiveExperienceVideo'
import EmailCapture, { isEmailCaptured, getEventEmailCaptureKey } from './EmailCapture'
import OnDemandRibbon from './OnDemandRibbon'

export function getChatInfo (chatInfos) {
  const info = {}
  if (chatInfos) {
    const sendbirdChatInfo = chatInfos.find((e) => !e.get('sendbirdAppId').includes('|'))
    const pubNubChatInfo = chatInfos.find((e) => e.get('sendbirdAppId').includes('|'))
    if (pubNubChatInfo) {
      const pubNubKeys = pubNubChatInfo.get('sendbirdAppId')
      const pubNubChannels = pubNubChatInfo.get('sendbirdChannelId')
      if (pubNubKeys) {
        [info.pubNubPublishKey, info.pubNubSubscribeKey] = pubNubKeys.split('|')
      }
      if (pubNubChannels) {
        [info.pubNubMainChannel, info.pubNubTechChannel] = pubNubChannels.split('|')
      }
    }
    info.sendbirdAppId = sendbirdChatInfo && sendbirdChatInfo.get('sendbirdAppId')
  }
  return info
}

const isRibbonDisabled = (key) => {
  return key && isBrowser()
    ? getLocalStorage(key) === 'yes' : true
}

const LiveExperience = (props) => {
  const {
    getChatList, uid, auth, chatInfo, staticText, user, currentUserPicture,
    eventId, username, isLiveAccessMember, canChat, showEmailCapture,
    onDemandRibbonKey, onDemandRibbonText, chatVisible, setChatVisible, reloadEvent,
  } = props
  const { enabled: communityEnabled } = useCommunityEnabled()
  useEffect(() => {
    getChatList(eventId)
  }, [eventId])
  useEffect(() => {
    setChatVisible(true)
    return () => {}
  }, [])
  const [showRibbon, setShowRibbon] = useState(!isRibbonDisabled(onDemandRibbonKey))

  const onDemandRibbonHandler = () => {
    setLocalStorage(onDemandRibbonKey, 'yes', uid, auth)
    setShowRibbon(!isRibbonDisabled(onDemandRibbonKey))
  }

  const imageUrl = communityEnabled && currentUserPicture
    ? currentUserPicture.small
    : user.getIn(['data', 'profile', 'picture', 'hdtv_190x266'])

  return (
    <div className="live-experience">
      {showRibbon && isLiveAccessMember && onDemandRibbonText
        && <OnDemandRibbon onClick={onDemandRibbonHandler} ribbonText={onDemandRibbonText} />}
      <LiveExperienceVideo reloadEvent={reloadEvent} />
      {
        showEmailCapture && <EmailCapture />
      }
      <div className={`live-experience-chat ${chatVisible ? '' : 'live-experience-chat--hidden'}`}>
        {chatInfo.pubNubSubscribeKey
          ? <Chat
              username={canChat ? username : ''}
              imageUrl={imageUrl}
              pubNubMainChannel={chatInfo.pubNubMainChannel}
              pubNubPublishKey={chatInfo.pubNubPublishKey}
              pubNubSubscribeKey={chatInfo.pubNubSubscribeKey}
              pubNubTechChannel={chatInfo.pubNubTechChannel}
              staticText={staticText}
              testing={global.BROWSER_TEST}
            />
          : `chatInfo for this event is missing!  ${chatInfo.toString()}`
        }
      </div>
    </div>
  )
}

LiveExperience.propTypes = {
  reloadEvent: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => {
      const currentUserPicture = selectCurrentUserProfilePicture(state)
      const language = state.user.getIn(['data', 'language', 0], 'en')
      const eventDetails = state.liveAccessEvents.getIn(['eventDetails', language,
        'data', 'event'])
      const eventId = eventDetails && eventDetails.get('id')
      const hasEntitlements = state.auth.get('subscriptions') && state.auth.get('subscriptions').size > 0
      const isLiveAccessMember = hasEntitlements && !!state.auth.get('subscriptions')
        && state.auth.get('subscriptions').includes(LIVE_ACCESS_SUBSCRIPTION_CODE)
      const isPublicStream = eventDetails && eventDetails.get('publicStream')
      const currentPath = state.resolver.get('path', '')
      const isAMARoute = currentPath === '/ama'
      // On /ama: email gate without entitlements; anonymous when active session allowAnon.
      // LivePage blocks anonymous when allowAnon is off on the active session.
      // On /live: show email gate to non-LiveAccess members (existing behavior)
      const shouldBlockEmailGate = isAMARoute
        // On /ama, block email gate if user has any subscription (monthly, annual, or AllAccess)
        ? hasEntitlements
        // On /live, block email gate if user has AllAccess/Gaia+
        : isLiveAccessMember
      const showEmailCapture = !shouldBlockEmailGate && !isPublicStream
        && eventDetails && !isEmailCaptured(getEventEmailCaptureKey(eventDetails))
      const onDemandRibbonKey = `ribbon-${eventDetails.get('route')}`
      const onDemandRibbonText = eventDetails.get('onDemandRibbonText')
      // For /ama: allow chat for all Gaia members (monthly, annual, AllAccess), block anonymous
      // For /live: only allow chat for AllAccess/Gaia+ members (existing behavior)
      const canChat = isAMARoute
        ? hasEntitlements // On /ama, allow chat if user has any subscription
        : isLiveAccessMember // On /live, only allow chat for AllAccess/Gaia+
      return {
        currentUserPicture,
        user: state.user,
        language,
        eventId,
        username: state.auth.get('username'),
        isLiveAccessMember,
        canChat,
        haveEmail: state.resolver.getIn(['query', 'haveEmail']) === 'true',
        showEmailCapture,
        chatVisible: state.liveAccessEvents.getIn([chatListStoreKey, 'visible']),
        chatInfo: getChatInfo(state.liveAccessEvents.getIn(['chatList',
          eventId,
          'data', 'chatInfo'])),
        onDemandRibbonKey,
        onDemandRibbonText,
        auth: state.auth,
        uid: (state.auth) ? state.auth.get('uid') : -1,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        getLiveAccessSchedule: actions.liveAccessEvents.getLiveAccessSchedule,
        getChatList: actions.liveAccessEvents.getLiveAccessChatList,
        getLiveAccessSessionDetails: actions.liveAccessEvents.getLiveAccessSessionDetails,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        setChatVisible: actions.liveAccessEvents.setChatVisible,
      }
    },
  ),
  connectStaticText({ storeKey: 'livePage' }),
)(LiveExperience)
